import { SvgIconTypeMap } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import React, { CSSProperties, ReactNode } from 'react';
import * as THREE from 'three';

import { EHistoryAction } from "../hooks/useUndoRedo";
import { IWall } from '../models/abstract/Wall';
import { IRelation } from '../models/Relation';
import AttributeModel, { IAttribute } from '../models/standard/AttributeModel';
import { ICompass } from '../models/standard/CompassModel';
import { IElement } from '../models/standard/ElementModel';
import FilesAPI2DModel from '../models/standard/FilesAPI2DModel';
import FilesAPIProductModel from '../models/standard/FilesAPIProductModel';
import { IModel2D } from '../models/standard/Model2DModel';
import { IProduct } from '../models/standard/ProductModel';
import { IRoom } from '../models/standard/RoomModel';
import UserModel from '../models/standard/UserModel';
import { NonFetchableEntity } from '../utils/planJsonExportImportUtils';
import {
  EActiveDrawer,
  EDirection,
  EElementDirection,
  EInteractionMode,
  EModal,
  EProgressType,
  EProgressVariant,
  ESubMenu,
  EUnitType,
  EWallMode
} from './Enums';
import { EAssetType, IFilesApiObject, IManufacturer } from './FilesAPIModels';
import { Side } from './Side';

/**
 * Props that can be used by any provider.
 *
 * @param children the child elements that will get wrapped with the provider.
 */
export interface IProviderProps {
  children: ReactNode[] | ReactNode;
}

/**
 * Enum to specify the high-level origin of the texture.
 * This helps distinguish if the texture data comes directly from a standalone asset or from a product.
 */
 export enum ETextureOrigin {
  ASSET = 'ASSET',
  PRODUCT = 'PRODUCT',
}

/**
 * Interface to describe the common properties shared by all texture types.
 *
 * @param name - name of the texture.
 * @param material - THREE.Texture instance that gets used for the materials/colors.
 * @param color - An optional string representing a color value.
 * @param rotation - An optional number specifying the rotation of the texture.
 * @param offset - An optional number defining the offset of the texture.
 * @param type - The specific type of the underlying asset (e.g., COLOR or MATERIAL). This property name is kept for import/export compatibility.
 */
export interface IBaseTexture {
  name: string;
  material: THREE.Texture | null;
  color?: string;
  rotation?: number;
  offset?: number;
  type: EAssetType.MATERIAL | EAssetType.COLOR | null;
}

/**
 * Interface for a texture that is sourced directly from a standalone asset.
 * It extends IBaseTexture and includes a specific ID for the asset and its textureOrigin.
 *
 * @param textureOrigin - Indicates that this texture's origin is a direct ASSET.
 * @param id - The unique identifier of the standalone asset itself.
 */
export interface IAssetTexture extends IBaseTexture {
  textureOrigin: ETextureOrigin.ASSET;
  id: number; 
}

/**
 * Interface for a texture that is sourced from a product.
 * It extends IBaseTexture and includes properties for both the product's ID and the specific texture asset within it.
 *
 * @param textureOrigin - Indicates that this texture's origin is a PRODUCT.
 * @param id - The unique identifier of the product.
 * @param assetId - The unique identifier of the specific asset *within* the product that provides this texture.
 * @param objects - categories from the filesapi
 * @param attributes - categories from the filesapi
*/
export interface IProductTexture extends IBaseTexture {
  textureOrigin: ETextureOrigin.PRODUCT;
  id: number;  
  assetId: number; 
  objects?: Array<IFilesApiObject>;
  attributes?: AttributeModel[];
  country?: string;
}

/**
 * The primary interface for a texture. An ITexture can be either a direct asset texture
 * (`IAssetTexture`) or a texture sourced from a product (`IProductTexture`).
 * The `textureOrigin` property helps determine which specific type it is.
 */
export type ITexture = IAssetTexture | IProductTexture;


/**
 * Interface to describe a tile model for any given floor.
 *
 * @param length number that describes the length of a single tile.
 * @param width number that describes the width of a single tile.
 * @param rotation number indicating the rotation of the tiles on the floor in degrees.
 * @param jointWidth number indicating the width of the joints between the tiles.
 * @param jointColor string indicating the color that is used for the joints.
 */
export interface ITiles {
  length: number;
  width: number;
  rotation: number;
  jointWidth: number;
  jointColor: string;
}


/**
 * Interface to describe a building.
 *
 * @param room compass object.
 * @param room IRoom object.
 * @param walls IWall array containing all the walls.
 * @param floor IFloor object describing the floor of the building.
 * @param products IProduct products in the building
 * @param elements IElement products in the building
 * @param models2D IModel2D models in the building.
 */
export interface IBuildingProviderContext {
  compass: ICompass,
  walls: IWall[];
  products: IProduct[];
  elements: IElement[];
  models2D: IModel2D[]; 
  isSetByUndoRedo: boolean;
  lastAction: EHistoryAction;
  nonFetchableEntities: NonFetchableEntity[];
  relations: IRelation[];
  rooms: IRoom[];
}

/**
 * Interface to describe the drawing context object.
 *
 * @param isDrawing boolean flag to indicate whether or not the user is currently drawing.
 * @param setIsDrawing function to change the indicator flag.
 * @param roomCompleted boolean flag to indicate if the room is completed or not.
 * @param setRoomCompleted function to change the indicator flag.
 */
export interface IDrawingContext {
  isDrawing: boolean;
  setIsDrawing: (flag: boolean) => void;
  roomCompleted: boolean;
  setRoomCompleted: (flag: boolean) => void;
}

/**
 * Interface for the modal context object.
 *
 * @param isModalShown boolean flag to indicate if the currently selected modal window will get rendered or not.
 * @param setIsModalShown function to change whether or not to show the currently selected modal.
 * @param activeModal EModal indicating which is the currently selected modal.
 * @param setActiveModal function to change the currently selected modal.
 * @param selectedRoomName string representing the name of the selected room.
 * @param setSelectedRoomName function to change the name of the selected room.
 * @param selectedRoomId number identifying the selected room.
 * @param setSelectedRoomId function to change the id of the selected room.
 */
export interface IModalContext {
  isModalShown: boolean;
  setIsModalShown: (isShown: boolean) => void;
  activeModal: EModal;
  setActiveModal: (newActive: EModal) => void;
  selectedRoomName: string;
  setSelectedRoomName: (newName: string) => void;
  selectedRoomId: number;
  setSelectedRoomId: (newRoomId: number) => void;
}

/**
 * Interface to describe the toolbar context object.
 *
 * @param currentlyActiveSubMenu ESubMenu enum to distinguish between the different sub menus and indicate which is currently active.
 * @param setCurrentlyActiveSubMenu Function to change the currently active sub menu.
 * @param interactionMode EInteractionMode enum to indicate the current interaction mode (e.g., move, select, comment, measure).
 * @param setInteractionMode Function to change the current interaction mode.
 * @param wallMode EWallMode enum to differentiate between different architecture modes.
 * @param setWallMode Function to change the current wall mode.
 * @param isDrawingAllowed Boolean flag to indicate if drawing is allowed.
 * @param setIsDrawingAllowed Function to toggle the drawing allowed state.
 */
export interface IToolbarContext {
  currentlyActiveSubMenu: ESubMenu;
  setCurrentlyActiveSubMenu: (newActive: ESubMenu) => void;
  interactionMode: EInteractionMode;
  setInteractionMode: (mode: EInteractionMode) => void;
  wallMode: EWallMode;
  setWallMode: (mode: EWallMode) => void;
  isDrawingAllowed: boolean;
  setIsDrawingAllowed: (allowed: boolean) => void;
}

/**
 * Interface for the MultiSelectToggle context object.
 *
 * @param selectedItems number array containing all ids of the currently selected items.
 * @param saveSelectedItems function to alter the selectedItems ID array.
 * @param showWallInnerLength boolean flag to indicate if the inner length of walls should get displayed.
 * @param setShowWallInnerLength function to alter the flag above.
 * @param showWallOuterLength boolean flag to indicate if the outer length of walls should get displayed.
 * @param setShowWallOuterLength function to alter the flag above.
 * @param showWindowLength boolean flag to indicate if the window length should get displayed.
 * @param setShowWindowLength function to alter the flag above.
 * @param showDoorLength boolean flag to indicate if the length of doors should get displayed.
 * @param setShowDoorLength function to alter the flag above.
 * @param showPassageLength boolean flag to indicate if the length of passages should get displayed.
 * @param setShowPassageLength function to alter the flag above.
 * @param showRadiatorLength boolean flag to indicate if the length of radiators should get displayed.
 * @param setShowRadiatorLength function to alter the flag above.
 * @param showRoomDetails boolean flag to indicate if the room details should get displayed.
 * @param setShowRoomDetails function to alter the flag above.
 * @param showAngles boolean flag to indicate if the angles should get displayed.
 * @param setShowAngles function to alter the flag above.
 * @param showRoomComponentDetails boolean flag to indicate if the length of room components should be displayed.
 * @param setShowRoomComponentDetails function to alter the flag above.
 * @param showElementDetails boolean flag to indicate if the element indicators are shown.
 * @param setShowElementDetails function to alter the flag above.
 * @param showMeasurements boolean flag to indicate if the measurements should get displayed.
 * @param setShowMeasurements function to alter the flag above.
 * @param showGeneralInfo boolean flag to indicate if the general infos should get displayed.
 * @param setShowGeneralInfo function to alter the flag above.
 * @param showElementDistances boolean flag to indicate if the distance between wall elements should get displayed.
 * @param setShowElementDistances function to alter the flag above.
 * @param showCameraPositionSprites boolean flag to indicate if the camera position sprite, that comes from importing a pano measurement, gets displayed
 * @param setShowCameraPositionSprites function to alter the flag above.
 * @param showHotspots boolean flag to indicate if the hotspots should get displayed.
 * @param setShowHotspots function to alter the flag above.
 * @param temporarilyHideAllElements function that temporarily sets all visibilities to false, executes a callback, and then restores the previous state.
 */
 export interface IMultiSelectToggleContext {
  selectedItems: number[];
  saveSelectedItems: (newSelected: number[]) => void;
  showWallInnerLength: boolean;
  setShowWallInnerLength: (showLength: boolean) => void;
  showWallOuterLength: boolean;
  setShowWallOuterLength: (showLength: boolean) => void;
  showWindowLength: boolean;
  setShowWindowLength: (showLength: boolean) => void;
  showDoorLength: boolean;
  setShowDoorLength: (showLength: boolean) => void;
  showPassageLength: boolean;
  setShowPassageLength: (showLength: boolean) => void;
  showRadiatorLength: boolean;
  setShowRadiatorLength: (showLength: boolean) => void;
  showRoomDetails: boolean;
  setShowRoomDetails: (showDetails: boolean) => void;
  showAngles: boolean;
  setShowAngles: (showAngles: boolean) => void;
  showRoomComponentDetails: boolean;
  setShowRoomComponentDetails: (showDetails: boolean) => void;
  showElementDetails: boolean;
  setShowElementDetails: (showElementDetails: boolean) => void;
  showMeasurements: boolean;
  setShowMeasurements: (showMeasurements: boolean) => void;
  showGeneralInfo: boolean;
  setShowGeneralInfo: (showInfo: boolean) => void;
  isSnappingEnabled: boolean;
  setIsSnappingEnabled: (snappingEnabled: boolean) => void;
  showElementDistances: boolean;
  setShowElementDistances: (showDistances: boolean) => void;
  showCameraPositionSprites: boolean;
  setShowCameraPositionSprites: (showCameraPositionSprite: boolean) => void;
  showHotspots: boolean;
  setShowHotspots: (showDistances: boolean) => void;
  temporarilyHideAllElements: <T>(callback: () => Promise<T> | T) => Promise<T>;
}

/**
 * Interface to describe the scene data context object.
 *
 * @param {boolean} sceneNeedsClearing boolean flag to indicate if the current scene needs to get cleared.
 * @param {(needsClearing: boolean) => void} setSceneNeedsClearing function to change the flag above.
 * @param {number} zoomData read only number to indicate the current zoom level.
 * @param {(zoom: number) => void} setZoomData function to alter the flag above.
 * @param {React.RefObject<THREE.Scene>} sceneRef reference to the scene object from THREE.
 * @param {(newRef: React.RefObject<THREE.Scene>) => void} setSceneRef function to change the scene reference object.
 * @param {number} rotateSceneContents numerical indicator if the elements in the scene need to get rotated, and if, by which amount.
 * @param {(rotationAngle: number) => void} setRotateSceneContents function to change the value above and induce a rotation of the contents of the current scene.
 */
export interface ISceneDataContext {
  sceneNeedsClearing: boolean;
  setSceneNeedsClearing: (needsClearing: boolean) => void;
  zoomData: number;
  setZoomData: (zoom: number) => void;
  sceneRef: React.RefObject<THREE.Scene>;
  setSceneRef: (newRef: React.RefObject<THREE.Scene>) => void;
  compassRotation: number;
  setCompassRotation: (newRotation: number) => void;
  rotateSceneContents: number;
  setRotateSceneContents: (rotationAngle: number) => void;
}

/**
 * Interface to represent a wall inside of the useSelectionHandlers custom hook.
 *
 * @param wallIndex number indicating which wall is the selected one.
 */
export interface ISelectedWall {
  wallIndex: number;
}

/**
 * Interface to represent a wall inside of the useSelectionHandlers custom hook.
 *
 * @param simpleMeasurementIndex number indicating which simple measurement is the selected one.
 */
export interface ISelectedSimpleMeasurement {
  simpleMeasurementIndex: number;
}

/**
 * Interface to represent a product inside of the useSelectionHandlers custom hook.
 *
 * @param selectedProductIndex number indicating which product is the selected one.
 */
export interface ISelectedProduct {
  selectedProductIndex: number;
}

/**
 * Interface to represent a 2d model inside of the useSelectionHandlers custom hook.
 *
 * @param selectedModel2DIndex number indicating which 2d model is the selected one.
 */
 export interface ISelectedModel2D {
  selectedModel2DIndex: number;
}

/**
 * Interface to reference a given window.
 *
 * @param wallIndex number representing the wall where the window is located.
 * @param windowIndex number representing the window in the wall.
 */
export interface ISelectedWindow {
  wallIndex: number;
  windowIndex: number;
}

/**
 * Interface to reference a given passage.
 *
 * @param wallIndex number representing the wall where the passage is located.
 * @param passageIndex number representing the passage in the wall.
 */
export interface ISelectedPassage {
  wallIndex: number;
  passageIndex: number;
}

/**
 * Interface to reference a given radiator.
 *
 * @param wallIndex number representing the wall where the radiator is located.
 * @param radiatorIndex number representing the radiator in the wall.
 */
 export interface ISelectedRadiator {
  wallIndex: number;
  radiatorIndex: number;
  side: Side;
}

/**
 * Interface to reference a given element.
 *
 * @param selectedElementIndex number representing the element in a room.
 */
export interface ISelectedElement {
  selectedElementIndex: number;
}

export interface ISelectedHotspot {
  hotspotId: number;
}

export interface ISelectedSpace {
  spaceId: string;
}

/**
 * Interface to reference a selected door.
 *
 * @param wallIndex number referencing the door that corresponds to the door.
 * @param doorIndex number indicating which index the door has inside of the wall.
 */
export interface ISelectedDoor {
  wallIndex: number;
  doorIndex: number;
}

/**
 * Interface to describe all selection handlers provided by our custom hook.
 *
 * @param selectedStandardWall object indicating which standard wall was selected.
 * @param selectedPartitionWall object indicating which partition wall was selected.
 * @param selectedSpace ISelectedSpace object to indicate if we have a selected room.
 * @param selectedWindow ISelectedWindow indicating which window was selected.
 * @param selectedDoor ISelectedDoor indicating which door is selected.
 * @param selectedPassage ISelectedPassage indicating which passage is selected.
 * @param selectedSimpleMeasurement ISelectedSimpleMeasurement indicating which simple measurement is selected.
 * @param selectedProduct ISelectedProduct indicating which product is selected.
 * @param selectedRadiator ISelectedRadiator indicating which radiator is selected.
 * @param selectedElement ISelectedElement indicating which element is selected.
 * @param selectedModel2D ISelectedModel2D indicating which 2d model is selected.
 * @param selectionAllowed boolean flag to indicate if a selection can be made.
 * @param activeDrawer EActiveDrawer enum to reference the active drawer.
 * @param setSelectionAllowed function to change the users ability to make a selection.
 * @param handleWallClick function to handle a click on a given wall.
 * @param handleRoomClick function to handle a click on a given room.
 * @param handleWindowClick function to handle a click on a given window.
 * @param handleDoorClick function to handle a click on a given door.
 * @param handlePassageClick function to handle a click on a given passage.
 * @param handlePartitionWallClick function to handle a click on a given partition wall.
 * @param handleSimpleMeasurementClick function to handle a click on a given simple measurement.
 * @param handleProductClick function to handle a click on a given product.
 * @param handleElementClick function to handle a click on a given element.
 * @param handleModel2DClick function to handle a click on a given 2d model.
 * @param setActiveDrawer function to change the active drawer.
 * @param resetSelection function to revert the selection back to its initial state.
 * @param hasSelection boolean getter function indicating if there is currently already a selection.
 * @param wallChangeDirection indicates which wall change mode is currently selected.
 * @param setWallChangeDirection function to change the wall change mode.
 */
export interface ISelectionHandlers {
  selectedStandardWall: ISelectedWall;
  selectedPartitionWall: ISelectedWall;
  selectedSpace: ISelectedSpace;
  selectedWindow: ISelectedWindow;
  selectedDoor: ISelectedDoor;
  selectedPassage: ISelectedPassage;
  selectedSimpleMeasurement: ISelectedSimpleMeasurement;
  selectedProduct: ISelectedProduct;
  selectedRadiator: ISelectedRadiator;
  selectedElement: ISelectedElement;
  selectedModel2D: ISelectedModel2D;
  selectedHotspot: ISelectedHotspot;
  selectionAllowed: boolean;
  activeDrawer: EActiveDrawer;
  setSelectionAllowed: (isAllowed: boolean) => void;
  handleWallClick: (wallIndex: number) => void;
  handleSpaceClick: (spaceUUID: string) => void;
  handleWindowClick: (wallIndex: number, windowIndex: number) => void;
  handleDoorClick: (wallIndex: number, doorIndex: number) => void;
  handlePassageClick: (wallIndex: number, passageIndex: number) => void;
  handlePartitionWallClick: (wallIndex: number) => void;
  handleSimpleMeasurementClick: (simpleMeasurementIndex: number) => void;
  handleProductClick: (productIndex: number) => void;
  handleModel2DClick: (model2DIndex: number) => void;
  handleRadiatorClick: (wallIndex: number, radiatorIndex: number, side: Side) => void;
  handleElementClick: (elementIndex: number) => void;
  handleHotspotClick: (hotspotId: number) => void;
  setActiveDrawer: (newActive: EActiveDrawer) => void;
  resetSelection: () => void;
  resetSelectionWithoutHistoryDrawer: () => void;
  hasSelection: () => boolean;
  hasSelectionWithoutDrawer: () => boolean;
  wallChangeDirection: EDirection;
  setWallChangeDirection: (newDirection: EDirection) => void;
  wallThicknessDirection: EDirection;
  setWallThicknessDirection: (dir: EDirection) => void;
}

/**
 * Interface for the selection context object.
 *
 * @param selectionHandlers ISelectionHandlers object containing all references to the individual handlers.
 */
export interface ISelectionContext {
  selectionHandlers: ISelectionHandlers;
}

/**
 * Interface to distinguish between the items located in the single select component.
 *
 * @param {OverridableComponent<SvgIconTypeMap<object, "svg">> | React.FunctionComponent<React.SVGProps<SVGSVGElement>>} icon MUI or custom icon reference to an svg icon object.
 * @param {string} text string text of the select item.
 * @param {number} id number identifier for the selected item.
 * @param {string} optional string reference for a custom icon. When both are provided, this will have higher priority compared to the mui icon.
 */
export interface ISingleSelectItem {
  icon: OverridableComponent<SvgIconTypeMap<object, 'svg'>> | React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  text: string;
  id: number;
}

/**
 * Interface for the single select context object.
 *
 * @param currentlySelected ISingleSelectItem indicating the currently selected option.
 * @param setCurrentlySelected function to select an item from the selection.
 * @param items array of ISelectItem representing the selectable items.
 * @param setItems function to update the list of selectable items.
 */
export interface ISingleSelectContext {
  currentlySelected: ISingleSelectItem;
  setCurrentlySelected: (newSelected: ISingleSelectItem) => void;
  items: ISingleSelectItem[];
  setItems: (newItems: ISingleSelectItem[]) => void;
}

/**
 * Interface for the dragging context object.
 *
 * @param isDragging boolean flag to indicate if the user is currently dragging.
 * @param setIsDragging function to change the boolean flag above.
 */
export interface IDraggingContext {
  isDragging: boolean;
  setIsDragging: (dragging: boolean) => void; 
}

/**
 * Interface to describe the language context object.
 *
 * @param locale string representing the current locale of the user.
 * @param setLocale function to change the current locale of the user.
 */
export interface ILanguageContext {
  locale: string;
  setLocale: (newLocale: string) => void;
}

/**
 * Interface for the user data context object.
 *
 * @param currentUserData UserModel representing a single user in our app.
 * @param setCurrentUserData function to change the current user data model.
 */
export interface IUserDataContext {
  currentUserData: UserModel;
  setCurrentUserData: (newUser: UserModel) => void;
}

/**
 * Interface to describe the door drawer props.
 *
 * @param open boolean flag to indicate if the drawer is open.
 * @param onClose function that will get called when the drawer gets closed.
 * @param selectedDoor ISelectedDoor object representing the selected door.
 * @param resetSelection function to reset the selection.
 * @param id string identifier for the door drawer.
 */
export interface DoorDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedDoor: ISelectedDoor;
  resetSelection: () => void;
  id: string;
}

/**
 * Interface for the FloorDrawer component props.
 *
 * @param open boolean flag to indicate if the drawer is currently open.
 * @param onClose function that will get called when the drawer gets closed.
 * @param resetSelection function to reset the current selection.
 * @param id string identifier for the door drawer.
 */
export interface FloorDrawerProps {
  open: boolean;
  onClose: () => void;
  resetSelection: () => void;
  id: string;
}

/**
 * Interface for the PartitionWallDrawer component props.
 *
 * @param open boolean flag to indicate if the drawer is open.
 * @param onClose function to get called when the drawer gets closed.
 * @param wallIndex number indicating the index of the wall inside of the partitionWalls array.
 * @param resetSelection function to reset the selection.
 * @param id string identifier for the partition wall drawer.
 */
export interface PartitionWallDrawerProps {
  open: boolean;
  onClose: () => void;
  wallIndex: number;
  resetSelection: () => void;
  id: string;
}

/**
 * Interface describing the props for the RoomComponentsDrawerProps component.
 *
 * @param id string identifier for the RoomComponentsDrawerProps drawer.
 * @param open boolean flag to indicate if the drawer is open or not.
 * @param onClose function that gets called when the drawer gets closed.
 */
export interface RoomComponentsDrawerProps {
  id: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Interface describing the props for the ProductDrawer component.
 *
 * @param open boolean flag to indicate if the drawer is open.
 * @param onClose function that will get called when the drawer gets closed.
 * @param selectedProduct ISelectedProduct object representing the selected product.
 * @param resetSelection function to reset the selection.
 * @param id string identifier for the product drawer.
 */
export interface ProductDrawerProps {
  id: string;
  open: boolean;
  onClose: () => void;
  selectedProduct: ISelectedProduct;
  resetSelection: () => void;
}

/**
 * Interface describing the props for the SimpleMeasurementDrawer component.
 *
 * @param id string identifier for the product drawer.
 * @param open boolean flag to indicate if the drawer is open or not.
 * @param onClose function that gets called when the drawer gets closed.
 */
export interface SimpleMeasurementDrawerProps {
  id: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Interface for the StandardWallDrawer component props.
 *
 * @param open boolean flag to indicate if the drawer is open.
 * @param onClose function to get called when the drawer gets closed.
 * @param wallIndex number indicating the index of the wall.
 * @param resetSelection function to reset the selection.
 * @param id string identifier for the Standard wall drawer.
 */
export interface StandardWallDrawerProps {
  open: boolean;
  onClose: () => void;
  relatedWallIndex?: number;
  resetSelection: () => void;
  id: string;
}

/**
 * Interface describing the props for the TextureDrawer component.
 *
 * @param id string identifier for the texture drawer.
 * @param open boolean flag to indicate if the drawer is open or not.
 * @param onClose function that gets called when the drawer gets closed.
 */
export interface TextureDrawerProps {
  id: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Interface for the WindowDrawer component props.
 *
 * @param open boolean flag to indicate if the drawer is opened.
 * @param onClose function that will get called when the drawer gets closed.
 * @param selectedWindow ISelectedWindow indicating which window is currently selected.
 * @param resetSelection function to reset the current selection.
 * @param id string indentifier of the window drawer component.
 */
export interface WindowDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedWindow: ISelectedWindow;
  resetSelection: () => void;
  id: string;
}

/**
 * Interface for the PassageDrawer component props.
 *
 * @param open boolean flag to indicate if the drawer is opened.
 * @param onClose function that will get called when the drawer gets closed.
 * @param selectedPassage ISelectedPassage indicating which passage is currently selected.
 * @param resetSelection function to reset the current selection.
 * @param id string indentifier of the passage drawer component.
 */
export interface PassageDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedPassage: ISelectedPassage;
  resetSelection: () => void;
  id: string;
}

/**
 * Interface describing the props for the ArchitectureDrawer component.
 *
 * @param id string identifier for the Architecture drawer.
 * @param open boolean flag to indicate if the drawer is open or not.
 * @param onClose function that gets called when the drawer gets closed.
 */
export interface ArchitectureDrawerProps {
  id: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Interface for the props of the Intro component.
 *
 * @param onEnd function to get called when the intro has finished.
 */
export interface IntroProps {
  onEnd: () => void;
}

/**
 * Interface for the props of the MenuDrawer component.
 *
 * @param isOpen boolean flag to indicate if the component is open or not.
 * @param onClose function to get called when the menu drawer gets closed.
 */
export interface MenuDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Interface for the props object of the TilesTexture component.
 *
 * @param width number representing the width of the tiles texture.
 * @param length number representing the length of the tiles texture.
 * @param jointColor string color value that the tile joints use.
 * @param tileColor string color value that the tiles themselves use.
 * @param jointWidth number representing the width of the individual joints.
 * @param rotation number representing the amount of rotation of the tiles in degrees.
 * @param onTextureUpdate function to get called when the texture gets updated.
 */
export interface TilesTextureProps {
  width: number;
  length: number;
  jointColor: string;
  tileColor: string;
  jointWidth: number;
  rotation: number;
  onTextureUpdate: (texture: THREE.CanvasTexture) => void;
}

/**
 * Describes the interface of the ProductDataContext and it's default values.
 * This context/provider will get used to store the product data, that gets fetched from the imsFilesApi.
 * Will also contain the manufacturers that have already been fetched.
 *
 * @param {FilesAPIProductModel[]} products an array containing the models, representing single product entries from our database.
 * @param {(newProduct: FilesAPIProductModel) => void} addNewProduct function to add one individual new product to the previously loaded ones.
 * @param {(newProducts: FilesAPIProductModel[]) => void} setProducts function to overwrite any pre-existing products with another array.
 * @param {IManufacturer[]} manufacturers array containing all previously fetched manufacturers from our filesApi.
 * @param {(newManufacturers: IManufacturer[]) => void;} setManufacturerNames function to set the array mentioned above.
 * @param {IAttribute[]} attributes array containing all previously fetched attributes from our filesApi.
 * @param {(newAttributes: IAttribute[]) => void;} setAttributes function to set the array mentioned above.
 * @param {IFilesApiObject[]} categories array containing all previously fetched categories from our filesApi.
 * @param {(newCategories: IFilesApiObject[]) => void;} setCategories function to set the array mentioned above.
 * @param {number} loadProgress numerical value representing the loading progress. Ranges from 0 to the length of products[].
 * @param {(currentProgress: number) => void} setLoadProgress function to update the loading progress.
 * @param {number} loadCountTarget numerical value representing the total amount of products that can get loaded.
 * @param {(newLoadCountTarget: number) => void} setLoadCountTarget function to update the value above.
 * @param {boolean} isUserSearching boolean flag to indicate if the user is currently searching.
 * @param {(isUserSearching: boolean) => void} setIsUserSearching function to change the flag above.
 * @param {number} searchLoadTarget numerical value indicating how many products will get loaded by the currently active search.
 * @param {(newAmount: number) => void} setSearchLoadTarget function to set the number above.
 * @param {number} searchLoadProgress numerical value indicationg how many products of the currently active search have been loaded yet.
 * @param {(loaded: number) => void} setSearchLoadProgress function to change the value above.
 * @param {boolean} apiFetchCompleted flag to indicate if the products have been fetched completely from our api endpoint.
 * @param {(completed: boolean) => void} setApiFetchCompleted flag to indicate if the products have been fetched completely from our api endpoint.
 */
export interface IProductDataContext {
  products: FilesAPIProductModel[];
  addNewProduct: (newProduct: FilesAPIProductModel) => void;
  setProducts: (newProducts: FilesAPIProductModel[]) => void;
  manufacturers: IManufacturer[];
  setManufacturers: (newManufacturers: IManufacturer[]) => void;
  attributes: IAttribute[];
  setAttributes: (newAttributes: IAttribute[]) => void;
  categories: IFilesApiObject[];
  setCategories: (newCategories: IFilesApiObject[]) => void;
  loadProgress: number;
  setLoadProgress: (currentProgress: number) => void;
  loadCountTarget: number;
  setLoadCountTarget: (newLoadCountTarget: number) => void;
  isUserSearching: boolean;
  setIsUserSearching: (isUserSearching: boolean) => void;
  searchLoadTarget: number;
  setSearchLoadTarget: (newAmount: number) => void;
  searchLoadProgress: number;
  setSearchLoadProgress: (loaded: number) => void;
  apiFetchCompleted: boolean;
  setApiFetchCompleted: (completed: boolean) => void;
}

/**
 * Union type to represent the two different types of progress components and the possibly undefined initial state.
 * - determinate - controlled progress bar, that accurately displays the progress.
 * - indeterminate - uncontrolled progress bar, that only shows when the loading has finished.
 */
export type TProgressVariant = 'indeterminate' | 'determinate' | undefined;

/**
 * Interface that describes the props object to create a new ImsProgress component.
 *
 * @param {EProgressVariant} variant optional progress variant to get created. Defaults to indeterminate.
 * @param {EElementDirection} direction optional direction needed for setting up the linear progress. Defaults to leftToRight.
 * @param {EProgressType} type optional progress type to get created. Defaults to circular.
 * @param {number} min optional minimum value that is equivalent to 0%. Defaults to 0.
 * @param {number} max optional minimum value that is equivalent to 100%. Defaults to 100.
 * @param {number} value optional controlled value. Needed for determinate variant. Defaults to 0.
 * @param {boolean} showLabel optional flag indicating if the label should get displayed on the determinate component. Defaults to true.
 * @param {string} labelUnit optional unit that will get appended to the label. Defaults to "%".
 * @param {string} progressColor optional string color value for the actual progress bar itself. Defaults to immersightBlue.
 * @param {string} labelColor optional string color value for the label. Defaults to black.
 * @param {string} backgroundColor optional string color value for the progress bar background. Defaults to "transparent".
 * @param {CSSProperties} style optional style object cotaining css rules for further customization.
 */
export interface ImsProgressProps {
  variant?: EProgressVariant;
  direction?: EElementDirection;
  type?: EProgressType;
  min?: number;
  max?: number;
  value?: number;
  showLabel?: boolean;
  labelUnit?: string;
  progressColor?: string;
  labelColor?: string;
  backgroundColor?: string;
  style?: CSSProperties;
}

/**
 * Enum to differentiate between the two different unit systems.
 * Describes the additional information that are needed for the export of the current floor planner state to file.
 *
 * @param {string} fileName the file name of the final exported file.
 * @param {string} projectTitle the title of the project as a string.
 * @param {Date} creationDate the date object, representing the time when this file was exported.
 */
export interface IFileInfo {
  fileName: string;
  projectTitle: string;
  creationDate: Date;
}

/**
 * Interface to be used for the UnitProvider Context.
 *
 * @param {EUnitType} unitType the type of measurement units that we want to use. Either metric or imperial.
 * @param {(newUnitType: EUnitType) => void} setUnitType function to change the unit type.
 */
export interface IUnitContext {
  unitType: EUnitType;
  setUnitType: (newUnitType: EUnitType) => void;
}

/**
 * Interface to describe the props object that is used to create a new ProductImageListItem component.
 *
 * @param {FilesAPIProductModel} product the product that should get represented by the component.
 * @param {number} index the index of the item inside of the product gallery image item list.
 */
export interface ProductImageListItemProps {
  product: FilesAPIProductModel;
  index: number;
}

/**
 * Interface to describe the props object that is used to create a new ProductSearchPanel.
 *
 * @param {(searchResults: FilesAPIProductModel | FilesAPI2DModel [] ) => void} returnResults function to propagate the search results to the parent component.
 * @param {IFilesApiObject[]} categories the categories that have been fetched from the api.
 * @param {IAttribute[]} attributes the attributes that have been fetched from the api.
 * @param {IManufacturer[]} manufacturers the manufacturers that have been fetched from the api.
 */
export interface ProductSearchPanelProps {
  returnResults: (searchResults: (FilesAPIProductModel | FilesAPI2DModel)[]) => void;
  categories: IFilesApiObject[];
  products: FilesAPIProductModel[]; 
  attributes: IAttribute[];
  manufacturers: IManufacturer[];
  showManufacturersFilter: boolean;
}

/* Interface describing the ZoomChangeContext that provides values related to the changes in zoom data.
 *
 * @param {number} indicatorLabelFontSize the font size that will get applied to the labels for different indicators in the scene.
 * @param {(newFontSize: number) => void} setIndicatorLabelFontSize function to change the value above.
 * @param {number} indicatorLabelBoxPadding the font size that will get applied to the labels for different indicators in the scene.
 * @param {(newPadding: number) => void} setIndicatorLabelBoxPadding function to change the value above.
 * @param {number} staggeredZoomData debounced zoom data value retrieved from the THREE orbit controls and debounced to certain intervals.
 */
export interface IZoomChangeContext {
  indicatorLabelFontSize: number;
  setIndicatorLabelFontSize: (newFontSize: number) => void;
  indicatorLabelBoxPadding: number;
  setIndicatorLabelBoxPadding: (newPadding: number) => void;
  staggeredZoomData: number;
}
